"use client";

import React, { useState } from "react";
import { GitBranch, GitMerge, GitPullRequest, Copy, Check, Terminal, Sparkles, AlertCircle } from "lucide-react";
import { CodeHighlight } from "@/components/CodeHighlight";

interface WorkflowConfig {
  id: string;
  name: string;
  badge: string;
  cycleTime: string;
  description: string;
  branchModel: string;
  mergeStrategy: "Squash & Merge" | "Rebase" | "Merge Commit";
  commands: string[];
  visualFlow: string[];
}

const WORKFLOWS: WorkflowConfig[] = [
  {
    id: "trunk",
    name: "Trunk-Based Development",
    badge: "Empfohlen für CI/CD",
    cycleTime: "< 1 Tag Branch-Lebensdauer",
    description: "Kurzlebige Feature-Branches, direkte Integration in main, unfertiger Code hinter Feature Flags.",
    branchModel: "main ➔ short-lived branch ➔ main",
    mergeStrategy: "Squash & Merge",
    visualFlow: [
      "main       ●─────●─────────● (Release)",
      "             \\   ↑ (Squash)",
      "feat/fast     ●─●┘"
    ],
    commands: [
      "# 1. Neuesten Stand holen",
      "git checkout main && git pull --rebase",
      "# 2. Kurzlebigen Branch anlegen (max. 1 Tag alt)",
      "git checkout -b feat/user-quickfix",
      "# 3. Kleine, atomare Commits",
      "git commit -am 'feat: implement small atomic unit'",
      "# 4. Vor PR lokal mit main rebasen",
      "git fetch origin && git rebase origin/main",
      "# 5. In main integrieren (Squash & Merge via PR)",
      "git push origin feat/user-quickfix"
    ],
  },
  {
    id: "feature",
    name: "Feature Branch Workflow",
    badge: "Standard für Web-Teams",
    cycleTime: "1–3 Tage",
    description: "Klassischer Pull-Request-Workflow mit Peer-Review vor dem Merge. Linearer Verlauf durch Squash.",
    branchModel: "main ➔ feature/[ticket] ➔ PR ➔ main",
    mergeStrategy: "Squash & Merge",
    visualFlow: [
      "main          ●───────────────● (Clean History)",
      "                \\           ↑ (PR Approval)",
      "feature/auth     ●──●──●──●─┘"
    ],
    commands: [
      "# 1. Feature-Branch vom aktuellen main erstellen",
      "git checkout main && git pull",
      "git checkout -b feature/AUTH-102-oauth2",
      "# 2. Conventional Commits verfassen",
      "git commit -m 'feat(auth): add google oauth2 provider'",
      "# 3. Nach Review mit PR mergen und Branch löschen",
      "git push -u origin feature/AUTH-102-oauth2",
      "# Nach dem PR-Merge:",
      "git checkout main && git pull --prune"
    ],
  },
  {
    id: "gitflow",
    name: "Gitflow (Release Trains)",
    badge: "Versionierte Software",
    cycleTime: "Wochen / Sprints",
    description: "Feste Entwicklungs- (develop) und Produktions-Branches (main) mit dedizierten Releases und Hotfixes.",
    branchModel: "main ➔ develop ➔ release/* ➔ main + develop",
    mergeStrategy: "Merge Commit",
    visualFlow: [
      "main       ●───────────────────● (v1.2.0)",
      "            \\                 ↑",
      "develop      ●───●──●───●─────●",
      "                  \\    ↑",
      "feature            ●──●┘"
    ],
    commands: [
      "# 1. Neues Feature zweigt von develop ab",
      "git checkout develop && git checkout -b feature/cart-v2",
      "# 2. Release-Vorbereitung (Versionsbump, Freeze)",
      "git checkout -b release/1.2.0 develop",
      "# 3. Release landet in main UND develop",
      "git checkout main && git merge --no-ff release/1.2.0",
      "git tag -a v1.2.0 -m 'Release 1.2.0'",
      "git checkout develop && git merge --no-ff release/1.2.0"
    ],
  },
  {
    id: "hotfix",
    name: "Production Hotfix",
    badge: "Notfall-Patch",
    cycleTime: "< 2 Stunden",
    description: "Dringender Patch zweigt direkt vom Produktions-Tag auf main ab und wird sofort bereitgestellt.",
    branchModel: "main (v1.2.0) ➔ hotfix/* ➔ main (v1.2.1)",
    mergeStrategy: "Rebase",
    visualFlow: [
      "main (v1.2.0) ──●─────────● (v1.2.1 Pushed)",
      "                 \\       ↑ (Fast-Forward/Rebase)",
      "hotfix/security   ●──────┘"
    ],
    commands: [
      "# 1. Direkt vom Produktionsstand abzweigen",
      "git checkout main && git pull",
      "git checkout -b hotfix/SEC-patch",
      "# 2. Bugfix isolieren und testen",
      "git commit -am 'fix(security): sanitize sql input'",
      "# 3. Taggen und sofort nach main deployen",
      "git checkout main && git merge --ff-only hotfix/SEC-patch",
      "git tag -a v1.2.1 -m 'Hotfix: security vulnerability patch'",
      "git push origin main --tags"
    ],
  },
];

export default function GitWorkflowPlayground() {
  const [selectedId, setSelectedId] = useState<string>("trunk");
  const [copied, setCopied] = useState(false);

  const current = WORKFLOWS.find((w) => w.id === selectedId) || WORKFLOWS[0];

  const handleCopy = () => {
    navigator.clipboard.writeText(current.commands.join("\n"));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex flex-col gap-5 p-4 sm:p-6 min-h-[380px]">
      {/* Workflow Preset Switcher - Mobile First Pills */}
      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-2 block">
          Wähle dein Team-Szenario:
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {WORKFLOWS.map((wf) => {
            const isActive = wf.id === selectedId;
            return (
              <button
                key={wf.id}
                type="button"
                onClick={() => setSelectedId(wf.id)}
                className={`flex flex-col items-start justify-center p-3 rounded-xl border text-left transition-all min-h-[52px] ${
                  isActive
                    ? "border-blue-500 bg-blue-500/10 text-blue-900 dark:text-blue-200 dark:border-blue-500/50 shadow-xs"
                    : "border-neutral-200/80 bg-neutral-50 hover:bg-neutral-100 text-neutral-700 dark:border-neutral-800 dark:bg-neutral-900/60 dark:hover:bg-neutral-800/80 dark:text-neutral-300"
                }`}
              >
                <span className="font-heading font-bold text-xs sm:text-sm line-clamp-1">
                  {wf.name}
                </span>
                <span className="text-[10px] text-neutral-500 dark:text-neutral-400 font-mono mt-0.5">
                  {wf.badge}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Meta Card: Strategy & Visual Flow */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 rounded-xl border border-neutral-200/80 bg-neutral-50/50 p-4 dark:border-neutral-800 dark:bg-neutral-950/40">
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <GitBranch className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <h3 className="font-heading text-sm font-bold text-neutral-900 dark:text-white">
              {current.name}
            </h3>
          </div>
          <p className="text-xs text-neutral-600 dark:text-neutral-300 leading-relaxed mb-3">
            {current.description}
          </p>
          <div className="flex flex-wrap gap-2 text-[11px]">
            <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-neutral-200/80 dark:bg-neutral-800 font-mono text-neutral-700 dark:text-neutral-300">
              Zyklus: {current.cycleTime}
            </span>
            <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-500/20 font-mono">
              Merge: {current.mergeStrategy}
            </span>
          </div>
        </div>

        {/* ASCII Branch Visualizer */}
        <div className="rounded-lg bg-neutral-900 p-3 font-mono text-xs text-emerald-400 dark:bg-black/80 flex flex-col justify-center overflow-x-auto">
          <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1">
            Commit-Graph Topologie:
          </div>
          {current.visualFlow.map((line, idx) => (
            <div key={idx} className="whitespace-pre font-mono leading-tight">
              {line}
            </div>
          ))}
        </div>
      </div>

      {/* Terminal Command Output with 1-Click Copy */}
      <div className="rounded-xl border border-neutral-300 dark:border-neutral-800 bg-neutral-950 text-neutral-100 overflow-hidden shadow-sm">
        <div className="flex items-center justify-between border-b border-neutral-800 bg-neutral-900/90 px-4 py-2 text-xs">
          <div className="flex items-center gap-2 text-neutral-400 font-mono">
            <Terminal className="h-3.5 w-3.5 text-blue-400" />
            <span>Bash / Zsh Kommandos</span>
          </div>
          <button
            type="button"
            onClick={handleCopy}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-neutral-800 hover:bg-neutral-700 text-xs font-medium text-neutral-200 transition-colors min-h-[32px]"
          >
            {copied ? (
              <>
                <Check className="h-3.5 w-3.5 text-emerald-400" />
                <span className="text-emerald-400 font-semibold">Kopiert!</span>
              </>
            ) : (
              <>
                <Copy className="h-3.5 w-3.5" />
                <span>Befehle kopieren</span>
              </>
            )}
          </button>
        </div>
        <CodeHighlight
          code={current.commands.join("\n")}
          language="bash"
          showLineNumbers={false}
          className="p-4 text-xs font-mono leading-relaxed"
        />
      </div>
    </div>
  );
}
